import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useSharing, type PermissionLevel } from "@/hooks/use-sharing";
import { useSubscription } from "@/hooks/use-subscription";
import { FeatureGate } from "@/components/shared/FeatureGate";
import {
  Users,
  Mail,
  Send,
  Loader2,
  UserPlus,
  Shield,
  Eye,
  Pencil,
  X,
  Clock,
  Check,
  UserMinus,
  LogOut,
  Lock,
} from "lucide-react";

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function PermissionBadge({ level }: { level: PermissionLevel }) {
  if (level === "full") {
    return (
      <Badge variant="default" className="gap-1">
        <Pencil className="h-3 w-3" />
        Acesso total
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1">
      <Eye className="h-3 w-3" />
      Apenas visualizar
    </Badge>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "accepted") {
    return (
      <Badge variant="outline" className="gap-1 text-green-600 border-green-600">
        <Check className="h-3 w-3" />
        Ativo
      </Badge>
    );
  }
  if (status === "pending") {
    return (
      <Badge variant="outline" className="gap-1 text-yellow-600 border-yellow-600">
        <Clock className="h-3 w-3" />
        Pendente
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 text-red-600 border-red-600">
      <X className="h-3 w-3" />
      Rejeitado
    </Badge>
  );
}

export function SharingSection() {
  return (
    <FeatureGate feature="account_sharing">
      <SharingSectionContent />
    </FeatureGate>
  );
}

function SharingSectionContent() {
  const {
    myShares,
    sharedWithMe,
    myInvitations,
    pendingAccess,
    loading,
    createInvitation,
    revokeShare,
    leaveShare,
    updatePermission,
    cancelInvitation,
    acceptShare,
    rejectShare,
  } = useSharing();

  const [email, setEmail] = useState("");
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>("view");
  const [isSending, setIsSending] = useState(false);
  const { isWithinLimit } = useSubscription();

  const currentShareCount = myShares.length + myInvitations.length;
  const inviteLocked = !isWithinLimit('max_shared_users', currentShareCount);

  const handleInvite = async () => {
    if (!email.trim()) return;
    
    setIsSending(true);
    const result = await createInvitation({
      email: email.trim(),
      permission_level: permissionLevel,
    });
    
    if (result) {
      setEmail("");
      setPermissionLevel("view");
    }
    setIsSending(false);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Compartilhamento
        </CardTitle>
        <CardDescription>
          Compartilhe sua conta com outras pessoas
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Invite Form */}
        <div className="space-y-4">
          <Label className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Convidar por email
          </Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="email@exemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSending}
              />
            </div>
            <Select
              value={permissionLevel}
              onValueChange={(v) => setPermissionLevel(v as PermissionLevel)}
              disabled={isSending}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="view">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Apenas visualizar
                  </div>
                </SelectItem>
                <SelectItem value="full">
                  <div className="flex items-center gap-2">
                    <Pencil className="h-4 w-4" />
                    Acesso total
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleInvite} disabled={isSending || !email.trim() || inviteLocked}>
              {inviteLocked ? (
                <Lock className="h-4 w-4" />
              ) : isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          {inviteLocked ? (
            <p className="text-xs text-yellow-600">
              <Lock className="h-3 w-3 inline mr-1" />
              Você atingiu o limite de usuários compartilhados do seu plano. Faça upgrade para convidar mais pessoas.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              <Shield className="h-3 w-3 inline mr-1" />
              <strong>Visualizar:</strong> pode ver todas as informações. <strong>Acesso total:</strong> pode adicionar, editar e excluir dados.
            </p>
          )}
        </div>

        {/* Pending Invitations Sent */}
        {myInvitations.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4" />
                Convites enviados ({myInvitations.length})
              </Label>
              <div className="space-y-2">
                {myInvitations.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>{inv.invitee_email[0].toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{inv.invitee_email}</p>
                        <p className="text-xs text-muted-foreground">
                          Expira em {new Date(inv.expires_at).toLocaleDateString("pt-BR")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <PermissionBadge level={inv.permission_level as PermissionLevel} />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => cancelInvitation(inv.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Pending Access Requests (to accept/reject) */}
        {pendingAccess.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-yellow-600">
                <Clock className="h-4 w-4" />
                Convites recebidos ({pendingAccess.length})
              </Label>
              <div className="space-y-2">
                {pendingAccess.map((share) => (
                  <div
                    key={share.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={share.owner?.avatar_url || undefined} />
                        <AvatarFallback>{getInitials(share.owner?.full_name || null)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{share.owner?.full_name || "Usuário"}</p>
                        <p className="text-xs text-muted-foreground">
                          Quer compartilhar a conta com você
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <PermissionBadge level={share.permission_level as PermissionLevel} />
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-600 hover:bg-green-50"
                        onClick={() => acceptShare(share.id)}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Aceitar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                        onClick={() => rejectShare(share.id)}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Recusar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* My Shares (people I shared with) */}
        {myShares.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Pessoas com acesso à minha conta ({myShares.length})
              </Label>
              <div className="space-y-2">
                {myShares.map((share) => (
                  <div
                    key={share.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={share.shared_with?.avatar_url || undefined} />
                        <AvatarFallback>{getInitials(share.shared_with?.full_name || null)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{share.shared_with?.full_name || "Usuário"}</p>
                        <StatusBadge status={share.status} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={share.permission_level}
                        onValueChange={(v) => updatePermission(share.id, v as PermissionLevel)}
                      >
                        <SelectTrigger className="w-[160px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">
                            <div className="flex items-center gap-2">
                              <Eye className="h-3 w-3" />
                              Visualizar
                            </div>
                          </SelectItem>
                          <SelectItem value="full">
                            <div className="flex items-center gap-2">
                              <Pencil className="h-3 w-3" />
                              Acesso total
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive">
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remover acesso?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {share.shared_with?.full_name || "Este usuário"} não poderá mais acessar sua conta.
                              Você pode convidá-lo novamente depois.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => revokeShare(share.id)}
                            >
                              Remover acesso
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Shared With Me (accounts I have access to) */}
        {sharedWithMe.filter(s => s.status === "accepted").length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Contas que tenho acesso ({sharedWithMe.filter(s => s.status === "accepted").length})
              </Label>
              <div className="space-y-2">
                {sharedWithMe.filter(s => s.status === "accepted").map((share) => (
                  <div
                    key={share.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={share.owner?.avatar_url || undefined} />
                        <AvatarFallback>{getInitials(share.owner?.full_name || null)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{share.owner?.full_name || "Usuário"}</p>
                        <p className="text-xs text-muted-foreground">
                          Compartilhou a conta com você
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <PermissionBadge level={share.permission_level as PermissionLevel} />
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive">
                            <LogOut className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Sair do compartilhamento?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Você não terá mais acesso à conta de {share.owner?.full_name || "este usuário"}.
                              Ele precisará convidá-lo novamente.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => leaveShare(share.id)}
                            >
                              Sair
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Empty state */}
        {myShares.length === 0 && sharedWithMe.length === 0 && myInvitations.length === 0 && pendingAccess.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Você ainda não compartilhou sua conta com ninguém.</p>
            <p className="text-sm">Convide alguém usando o email acima.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
